// SlideCanvasRenderer.js - SIMPLIFIED: No validation overhead
import slideTemplates from './slideTemplates.json';

import {
  loadImage,
  clearImageCache,
  drawBackground,
  drawGridOverlay,
  drawWaveOverlay,
  drawMeshOverlay,
  drawGlassCard,
  drawImage,
  drawAccent,
  drawAccentBox,
  drawDecorativeLine,
  drawBarChart,
  drawLineChart,
  drawFloatingBlobs,
  drawMacOSDots
} from './CanvasDrawingUtils.js';
import {
  getTieredTemplate,
  setGlobalTiers,
  drawText,
  drawBulletPoints
} from './TextRenderingUtils.js';

// ===== QUALITY PRESETS =====
const QUALITY_PRESETS = {
  standard: 2,
  high: 3,
  ultra: 4
};

const SCALE_FACTOR = QUALITY_PRESETS.standard;

// ===== LOAD GLOBAL TIERS FROM JSON =====
const globalTiers = slideTemplates.globalTiers || null;
if (globalTiers) {
  setGlobalTiers(globalTiers);
}

// ===== RE-EXPORT UTILITIES =====
export { loadImage, clearImageCache };

// ===== MAIN RENDER FUNCTION =====
export const renderSlideToCanvas = async (canvas, slideContent, templateId = 'modern_bullets_right', imageUrl = null) => {
  const template = slideTemplates[templateId] || slideTemplates['modern_bullets_right'];
  
  const ctx = canvas.getContext('2d', { 
    alpha: false,
    willReadFrequently: false
  });
  
  const BASE_WIDTH = 1920;
  const BASE_HEIGHT = 1080;
  
  canvas.width = BASE_WIDTH * SCALE_FACTOR;
  canvas.height = BASE_HEIGHT * SCALE_FACTOR;
  
  ctx.scale(SCALE_FACTOR, SCALE_FACTOR);
  
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  // ✅ Use tier system instead of dynamic scaling
  const tieredTemplate = getTieredTemplate(ctx, slideContent, template, globalTiers);
  
  // 1. Draw background
  drawBackground(ctx, { width: BASE_WIDTH, height: BASE_HEIGHT }, tieredTemplate.background);
  
  // 2. Draw overlays
  if (tieredTemplate.gridOverlay) {
    drawGridOverlay(ctx, BASE_WIDTH, BASE_HEIGHT, tieredTemplate.gridOverlay);
  }
  if (tieredTemplate.waveOverlay) {
    drawWaveOverlay(ctx, BASE_WIDTH, BASE_HEIGHT, tieredTemplate.waveOverlay);
  }
  if (tieredTemplate.meshOverlay) {
    drawMeshOverlay(ctx, BASE_WIDTH, BASE_HEIGHT, tieredTemplate.meshOverlay);
  }
  

if (tieredTemplate.floatingBlobs) {
  drawFloatingBlobs(ctx, tieredTemplate.floatingBlobs);
}


  // 3. Draw glass card
  if (tieredTemplate.glassCard) {
    drawGlassCard(ctx, tieredTemplate.glassCard);
  }
  
  if (tieredTemplate.macOSDots) {
  drawMacOSDots(ctx, tieredTemplate.macOSDots);
}

  // 4. Draw image with filters/effects
  let loadedImage = null;
  if (imageUrl && tieredTemplate.supportsImage && tieredTemplate.image) {
    try {
      loadedImage = await loadImage(imageUrl);
      drawImage(ctx, loadedImage, tieredTemplate.image);
    } catch (error) {
      // Image loading failed - continue without image
    }
  }
  
  // 5. Draw accent elements
  if (tieredTemplate.accent) {
    drawAccent(ctx, tieredTemplate.accent);
  }
  if (tieredTemplate.accentBox) {
    drawAccentBox(ctx, tieredTemplate.accentBox);
  }
  if (tieredTemplate.accentLine || tieredTemplate.decorativeLine || tieredTemplate.decorativeElement) {
    drawDecorativeLine(ctx, tieredTemplate.accentLine || tieredTemplate.decorativeLine || tieredTemplate.decorativeElement);
  }
  if (tieredTemplate.goldLine || tieredTemplate.neonLine) {
    drawDecorativeLine(ctx, tieredTemplate.goldLine || tieredTemplate.neonLine);
  }
  if (tieredTemplate.colorBlock) {
    drawAccentBox(ctx, tieredTemplate.colorBlock);
  }
  
  let currentY = tieredTemplate.heading?.y || 120;
  
  // 6. Draw heading
  if (tieredTemplate.heading && slideContent.heading) {
    let headingConfig = { ...tieredTemplate.heading };
    
    if (slideContent.chartData) {
      const randomPosition = Math.random() < 0.5 ? 'left' : 'center';
      
      if (randomPosition === 'center') {
        headingConfig.y = 80;
        headingConfig.x = 960;
        headingConfig.align = 'center';
        headingConfig.maxWidth = 1600;
      } else {
        headingConfig.y = 80;
        headingConfig.x = 120;
        headingConfig.align = 'left';
        headingConfig.maxWidth = 1680;
      }
    }
    
    const headingHeight = drawText(ctx, slideContent.heading, headingConfig);
    currentY = headingConfig.y + headingHeight + 60;
  }
  
  // 7. Draw subheading
  if (tieredTemplate.subheading && slideContent.subheading) {
    const subConfig = { ...tieredTemplate.subheading, y: currentY };
    const subHeight = drawText(ctx, slideContent.subheading, subConfig);
    currentY += subHeight + 40;
  }
  
  // 8. Draw bullet points
  if (tieredTemplate.bulletPoints && slideContent.bulletPoints && slideContent.bulletPoints.length > 0) {
    const bulletConfig = { ...tieredTemplate.bulletPoints, y: currentY };
    drawBulletPoints(ctx, slideContent.bulletPoints, bulletConfig);
  }
  
  // 9. Draw charts
  if (slideContent.chartData) {
    const { type, values, labels, series } = slideContent.chartData;
    const isMultiSeries = series && Array.isArray(series) && series.length > 0;
    
    const barStyle = Math.random() < 0.5 ? 1 : 2;
    const lineStyle = Math.random() < 0.5 ? 1 : 2;
    
    const chartArea = tieredTemplate.chartArea || {
      x: 100,        
      y: 300,        
      width: 1720,   
      height: 700   
    };
    
    const chartConfig = {
      x: chartArea.x,
      y: chartArea.y,
      width: chartArea.width,
      height: chartArea.height,
      barColors: getChartColorsForTemplate(tieredTemplate, type),
      textColor: tieredTemplate.heading?.color || '#FFFFFF', 
      labels: labels,
      showGrid: true,
      showValues: true,
      style: type === 'bar' ? barStyle : lineStyle,
      isMultiSeries: isMultiSeries,
      series: isMultiSeries ? series : null
    };

    switch (type) {
      case 'bar':
        drawBarChart(ctx, isMultiSeries ? null : values, chartConfig);
        break;
      case 'line':
        drawLineChart(ctx, isMultiSeries ? null : values, chartConfig);
        break;
      default:
        // Unknown chart type - skip rendering
    }
  }
  
  return canvas;
};

const getChartColorsForTemplate = (template, chartType) => {
  // Just return the chart colors from the template, period.
  if (template.chartColors && template.chartColors[chartType]) {
    return template.chartColors[chartType];
  }
  
  // If no specific type, just return the first color array we find
  if (template.chartColors) {
    const firstColorArray = Object.values(template.chartColors)[0];
    if (Array.isArray(firstColorArray)) {
      return firstColorArray;
    }
  }
  
  // Absolute fallback - this should never happen
  return ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4'];
};

// ===== BATCH RENDER WITH IMAGES =====
export const renderSlidesWithImages = async (slides, slideTemplateIds, images) => {
  const renderedSlides = [];
  
  for (let i = 0; i < slides.length; i++) {
    const canvas = document.createElement('canvas');
    const templateId = slideTemplateIds[i] || 'modern_bullets_right';
    
    const hasChartData = slides[i].extractedContent?.chartData;
    let imageUrl = hasChartData ? null : (images[i]?.image?.url || null);
    
    try {
      await renderSlideToCanvas(canvas, slides[i].extractedContent, templateId, imageUrl);
      
      const imageData = canvas.toDataURL('image/png', 1.0);
      
      renderedSlides.push({
        slideIndex: i,
        imageData: imageData,
        template: templateId,
        hasImage: !!imageUrl && !hasChartData,
        hasChart: !!hasChartData,
        imageInfo: !hasChartData ? (images[i]?.image || null) : null,
        chartInfo: hasChartData ? slides[i].extractedContent.chartData : null
      });
    } catch (error) {
      renderedSlides.push({
        slideIndex: i,
        error: error.message
      });
    }
  }
  
  return renderedSlides;
};

// ===== BACKGROUND-ONLY RENDER =====
export const renderSlideBackgroundOnly = async (canvas, templateId = 'modern_bullets_right', imageUrl = null) => {
  const template = slideTemplates[templateId] || slideTemplates['modern_bullets_right'];
  
  const ctx = canvas.getContext('2d', { 
    alpha: false,
    willReadFrequently: false
  });
  
  const BASE_WIDTH = 1920;
  const BASE_HEIGHT = 1080;
  
  canvas.width = BASE_WIDTH * SCALE_FACTOR;
  canvas.height = BASE_HEIGHT * SCALE_FACTOR;
  
  ctx.scale(SCALE_FACTOR, SCALE_FACTOR);
  
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  
  drawBackground(ctx, { width: BASE_WIDTH, height: BASE_HEIGHT }, template.background);
  
  if (template.gridOverlay) {
    drawGridOverlay(ctx, BASE_WIDTH, BASE_HEIGHT, template.gridOverlay);
  }
  if (template.waveOverlay) {
    drawWaveOverlay(ctx, BASE_WIDTH, BASE_HEIGHT, template.waveOverlay);
  }
  if (template.meshOverlay) {
    drawMeshOverlay(ctx, BASE_WIDTH, BASE_HEIGHT, template.meshOverlay);
  }
  
  if (template.glassCard) {
    drawGlassCard(ctx, template.glassCard);
  }
  
  let loadedImage = null;
  if (imageUrl && template.supportsImage && template.image) {
    try {
      loadedImage = await loadImage(imageUrl);
      drawImage(ctx, loadedImage, template.image);
    } catch (error) {
      // Image loading failed - continue without image
    }
  }
  
  if (template.accent) {
    drawAccent(ctx, template.accent);
  }
  if (template.accentBox) {
    drawAccentBox(ctx, template.accentBox);
  }
  if (template.accentLine || template.decorativeLine || template.decorativeElement) {
    drawDecorativeLine(ctx, template.accentLine || template.decorativeLine || template.decorativeElement);
  }
  if (template.goldLine || template.neonLine) {
    drawDecorativeLine(ctx, template.goldLine || template.neonLine);
  }
  if (template.colorBlock) {
    drawAccentBox(ctx, template.colorBlock);
  }
  


// Add this BEFORE "return canvas;" in renderSlideBackgroundOnly:

if (template.floatingBlobs) {
  drawFloatingBlobs(ctx, template.floatingBlobs);
}

if (template.macOSDots) {
  drawMacOSDots(ctx, template.macOSDots);
}


  return canvas;
};

export const renderSlideBackgroundsOnly = async (slides, slideTemplateIds, images) => {
  const renderedBackgrounds = [];
  
  for (let i = 0; i < slides.length; i++) {
    const canvas = document.createElement('canvas');
    const templateId = slideTemplateIds[i] || 'modern_bullets_right';
    const imageUrl = images[i]?.image?.url || null;
    
    try {
      await renderSlideBackgroundOnly(canvas, templateId, imageUrl);
      
      const imageData = canvas.toDataURL('image/png', 1.0);
      
      renderedBackgrounds.push({
        slideIndex: i,
        imageData: imageData,
        template: templateId,
        hasImage: !!imageUrl
      });
    } catch (error) {
      renderedBackgrounds.push({
        slideIndex: i,
        error: error.message
      });
    }
  }
  
  return renderedBackgrounds;
};

// ===== TEMPLATE UTILITY FUNCTIONS =====
export const selectBestTemplate = (slideContent) => {
  if (slideContent.chartData) {
    return 'modern_bullets_right';
  }
  
  if (!slideContent.bulletPoints || slideContent.bulletPoints.length === 0) {
    return 'modern_bullets_right';
  }
  
  if (slideContent.bulletPoints.length <= 3) {
    return slideTemplates['glassmorphic_bullets_right'] ? 'glassmorphic_bullets_right' : 'modern_bullets_right';
  }
  
  return slideTemplates['pastel_bullets_right'] ? 'pastel_bullets_right' : 'modern_bullets_right';
};

export const getTemplatesList = () => {
  return Object.values(slideTemplates)
    .filter(t => t && t.id && typeof t.id === 'string')
    .map(t => ({
      id: t.id,
      name: t.name,
      category: t.category,
      supportsImage: t.supportsImage || false,
      supportsChart: t.supportsChart || false
    }));
};

export const getTemplatesByType = (slideContent) => {
  const allTemplates = getTemplatesList();
  
  if (slideContent.chartData) {
    return allTemplates.filter(t => t.supportsChart === true);
  }
  
  return allTemplates.filter(t => t.supportsImage === true);
};

export const downloadSlideAsImage = (canvas, slideIndex) => {
  const link = document.createElement('a');
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:.]/g, '-');
  
  const qualityName = Object.keys(QUALITY_PRESETS).find(
    key => QUALITY_PRESETS[key] === SCALE_FACTOR
  ) || 'custom';
  
  link.download = `slide-${slideIndex + 1}-${qualityName}-${canvas.width}x${canvas.height}-${timestamp}.png`;
  link.href = canvas.toDataURL('image/png', 1.0);
  link.click();
};

export const getCurrentQuality = () => {
  const qualityName = Object.keys(QUALITY_PRESETS).find(
    key => QUALITY_PRESETS[key] === SCALE_FACTOR
  );
  return {
    name: qualityName || 'custom',
    scale: SCALE_FACTOR,
    resolution: `${1920 * SCALE_FACTOR}x${1080 * SCALE_FACTOR}`,
    presets: QUALITY_PRESETS
  };
};

export const setQualityPreset = (presetName) => {
  if (QUALITY_PRESETS[presetName]) {
    return QUALITY_PRESETS[presetName];
  }
  return null;
};

export { exportSlideToPPTX, exportAllSlidesToPPTX } from '../exports/pptxExporter.js';